import { useContext, useEffect, useState } from "react";
import { RelatorioContent, RelatorioItem, Usina } from "../types";
import { CardIntervalOptionsEnum } from "../../dashboard/types";
import PrimaryBaseButton from "../../../main/components/PrimaryBaseButton";
import { Grid, Typography } from "@mui/material";
import { usinasApi } from "../../../services/api";
import { getDayMonthAndYearFromDate } from "../../../main/utils/utils";
import { Screen } from "../../../main/components/screen/Screen";
import RopTableSearch from "../../../main/components/RopTableSearch";
import InsertInvitationIcon from "@mui/icons-material/InsertInvitation";
import DateFilterModal from "../components/DateFilterModal";
import dayjs from "dayjs";
import { MainContext } from "../../../main/context/MainContextProvider";
import BaseButton from "../../../main/components/BaseButton";
import RestartAltIcon from "@mui/icons-material/RestartAlt";
import AddIcon from "@mui/icons-material/Add";
import GenerateReportModal from "../components/GenerateReportModal";
import ReprocessDailyReportModal from "../components/ReprocessDailyReportModal";
import ReportErrorsModal from "../components/ReportErrorsModal";
import HistoryList from "../components/HistoryList";
import AutorenewIcon from "@mui/icons-material/Autorenew";

export default function ReportList() {
    const {
        toastNotifications,
        setToastNotifications
    } = useContext(MainContext);

    const [loading, setLoading] = useState(true);
    const [openDateFilterModal, setOpenDateFilterModal] = useState(false);
    const [openGenerateReportModal, setOpenGenerateReportModal] = useState(false);
    const [openReprocessDailyReportModal, setOpenReprocessDailyReportModal] = useState(false);
    const [openReportErrorsModal, setOpenReportErrorsModal] = useState(false);
    const [selectedReport, setSelectedReport] = useState<RelatorioItem | null>(
        null
    );
    const [search, setSearch] = useState("");
    const [reportList, setReportList] = useState<RelatorioContent[]>([]);
    const [dateFilter, setDateFilter] = useState<dayjs.Dayjs | null>(null);
    const [interval, setInterval] = useState<CardIntervalOptionsEnum>(
        CardIntervalOptionsEnum.Mes
    );

    useEffect(() => {
        const controller = new AbortController();
        const { signal } = controller;

        mount(signal);

        return () => {
            controller.abort();
        };
    }, [interval]);

    const [usinas, setUsinas] = useState<Usina[]>([]);

    const mount = async (signal?: AbortSignal) => {
        try {
            setLoading(true);
            const response = await usinasApi.getUsinas();

            setUsinas(response.data);
        } catch (error) {
            throw error;
        } finally {
            setLoading(false);
        }
        
        if (dateFilter) {
            const dia = dateFilter?.date();
            const mes = dateFilter?.month() + 1;
            const ano = dateFilter?.year();

            const diaFormatado = dia < 10 ? `0${dia}` : dia;
            const mesFormatado = mes < 10 ? `0${mes}` : mes;

            const dataFormatada = `${ano}-${mesFormatado}-${diaFormatado ?? "01"}`;

            try {
                setLoading(true);
                const response = await usinasApi.getReportsByInterval(
                    interval,
                    dataFormatada,
                    signal
                );

                setReportList(response.data.relatorioList);
            } catch (error) {
                throw error;
            } finally {
                setLoading(false);
            }
        } else {
            try {
                setLoading(true);
                const response = await usinasApi.getReportsByInterval(interval, undefined, signal);
                setReportList(response.data.relatorioList);
            } catch (error) {
                throw error;
            } finally {
                setLoading(false);
            }
        }
    };

    useEffect(() => {
        let filteredUnifiedReports = reportList;
        if (search) {
            filteredUnifiedReports = reportList.filter((report) =>
                report.usinaName.toLowerCase().includes(search.toLowerCase())
            );
            setReportList(filteredUnifiedReports);
        } else mount();
    }, [search]);

    useEffect(() => {
        if(dateFilter == null){
            mount();
        }
    }, [dateFilter]);

    const GetFilteredData = async (dateFilter: dayjs.Dayjs | null) => {
        if (dateFilter) {
            const dia = dateFilter.date();
            const mes = dateFilter.month() + 1;
            const ano = dateFilter.year();

            const diaFormatado = dia < 10 ? `0${dia}` : dia;
            const mesFormatado = mes < 10 ? `0${mes}` : mes;

            const dataFormatada = `${ano}-${mesFormatado}-${diaFormatado ?? "01"}`;

            let response: any;
            try {
                setLoading(true);
                response = await usinasApi.getReportsByInterval(
                    interval,
                    dataFormatada
                );

                setReportList(response.data.relatorioList);
            } catch (error) {
                throw error;
            } finally {
                setLoading(false);
            }

            if (!(reportList.length > 0)) {
                const newNotifications = {
                    isAdding: true,
                    notifications: [
                        ...toastNotifications.notifications,
                        {
                            severity: "error" as const,
                            text: "Não foi encontrado nenhum relatório com a data indicada",
                        },
                    ],
                };
                setToastNotifications(newNotifications);
            }
        } else { setReportList(reportList); }
    };

    return (
        <Screen.Root>
            <Screen.Header>
                <Typography
                    variant="h4"
                    sx={{
                        fontWeight: "400",
                        paddingTop: "15px",
                        paddingBottom: "15px",
                    }}
                >
                    Relatórios
                </Typography>
            </Screen.Header>
            <Screen.Container>
                <Grid
                    sx={{
                        display: "flex",
                        columnGap: 1,
                        rowGap: 2,
                        width: "100%",
                        flexDirection: { xs: "column", sm: "row" },
                        justifyContent: "flex-end",
                        alignItems: "center",
                        marginTop: "8px",
                        marginBottom: "8px",
                    }}
                >
                    <Grid>
                        <BaseButton
                            onClick={() => {
                                setOpenGenerateReportModal(true);
                            }}
                            sx={{ height: "35px" }}
                            title="Gerar Relatório"
                            startIcon={<AddIcon />}
                        />
                    </Grid>
                    <Grid>
                        <BaseButton
                            onClick={() => {
                                setOpenReprocessDailyReportModal(true);
                            }}
                            sx={{ height: "35px" }}
                            title="Regenerar"
                            startIcon={<AutorenewIcon />}
                        />
                    </Grid>
                    <Grid>
                        <BaseButton
                            onClick={() => {
                                setDateFilter(null);
                                setSearch("");
                            }}
                            sx={{ height: "35px" }}
                            title="Limpar filtros"
                            startIcon={<RestartAltIcon />}
                        />
                    </Grid>
                    <Grid
                        sx={{
                            display: "flex",
                            columnGap: 1,
                            flexDirection: "row",
                            alignItems: "center",
                        }}
                    >
                        <PrimaryBaseButton
                            isActive={interval === CardIntervalOptionsEnum.Dia}
                            onClick={() => {
                                setInterval(CardIntervalOptionsEnum.Dia);
                                setDateFilter(null);
                                setSearch("");
                            }}
                            sx={{ height: "35px" }}
                            title="Relatórios Diários"
                        />
                        <PrimaryBaseButton
                            isActive={interval === CardIntervalOptionsEnum.Mes}
                            onClick={() => {
                                setInterval(CardIntervalOptionsEnum.Mes);
                                setDateFilter(null);
                                setSearch("");
                            }}
                            sx={{ height: "35px" }}
                            title="Relatórios Mensais"
                        />
                        <InsertInvitationIcon
                            onClick={() => {
                                setOpenDateFilterModal(true);
                            }}
                            sx={{
                                fontSize: "32px",
                                cursor: "pointer",
                                color: "#FFFFFF",
                            }}
                        />
                    </Grid>
                    <RopTableSearch
                        placeholder="Buscar por usina"
                        search={search}
                        setSearch={setSearch}
                    />
                </Grid>
                <Grid container>
                    <HistoryList
                        usinas={usinas}
                        loading={loading}
                        interval={interval}
                        reportItemList={reportList}
                        setOpenReportErrorsModal={setOpenReportErrorsModal}
                        setSelectedReportItem={setSelectedReport}
                        onMount={mount}
                    /></Grid>
            </Screen.Container>
            <DateFilterModal
                date={dateFilter}
                setDate={setDateFilter}
                interval={interval}
                open={openDateFilterModal}
                setOpen={setOpenDateFilterModal}
                title="Filtro por Data"
                onCLick={() => GetFilteredData(dateFilter)}
            />
            <GenerateReportModal
                interval={interval}
                open={openGenerateReportModal}
                setOpen={setOpenGenerateReportModal}
                onMount={mount}
            />
            <ReprocessDailyReportModal
                open={openReprocessDailyReportModal}
                setOpen={setOpenReprocessDailyReportModal}
                onMount={mount}
            />
            <ReportErrorsModal
                open={openReportErrorsModal}
                setOpen={setOpenReportErrorsModal}
                errors={selectedReport?.errors || []}
                usinaName={selectedReport?.usinaName || ""}
            />
        </Screen.Root>
    );
}

export function GetDateStringFromInterval(
    interval: CardIntervalOptionsEnum,
    date: Date | string
): string {
    const formatedDate = getDayMonthAndYearFromDate(date);

    if (interval == CardIntervalOptionsEnum.Dia) {
        return `${formatedDate.dia}/${formatedDate.mes}/${formatedDate.ano}`;
    }
    if (interval == CardIntervalOptionsEnum.Mes) {
        return `${formatedDate.mes}/${formatedDate.ano}`;
    }
    return "";
}
